import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";

import { Subscription } from "rxjs";

import { OverlayCommonService } from "@components/shared/overlay/overlay-common.service";
import { SlideOverlayPageService } from "@components/shared/overlay/slide-overlay-page/slide-overlay-page.service";
import { RiskAssessmentTemplateResourceType } from "@components/shared/risk-assessment-templates/constants";
import { NotificationService } from "@shared/services";
import { RouterService } from "@shared/services/router.service";
import { CommonUtils } from "@shared/utils";

import { RiskAssessmentReportsApiService } from "../../api";
import { RiskAssessmentReport, RiskAssessmentReportRecord } from "../../models";
import {
  AddRiskAssessmentReportDialogComponent,
  AddRiskAssessmentReportDialogData,
} from "../add-risk-assessment-report-dialog/";
import { RelatedRiskAssessmentReportsTableComponent } from "../related-risk-assessment-reports-table";
import { RiskAssessmentReportsTableComponent } from "../risk-assessment-reports-table/";

@Component({
  standalone: false,
  selector: "app-overlay-risk-assessment-reports",
  templateUrl: "./overlay-risk-assessment-reports.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverlayRiskAssessmentReportsComponent implements OnDestroy {
  private readonly dialog = inject(MatDialog);

  private readonly notification = inject(NotificationService);

  private readonly reportsApi = inject(RiskAssessmentReportsApiService);

  private readonly router = inject(RouterService);

  readonly overlay = inject(SlideOverlayPageService);

  readonly overlayService = input.required<OverlayCommonService>();

  readonly activeOrganisationId = input.required<string>();

  readonly recordUri = input.required<string>();

  readonly resourceType = input.required<RiskAssessmentTemplateResourceType>();

  readonly relatedRecords = input<RiskAssessmentReportRecord["record"][]>();

  readonly isLoading = input(false);

  readonly relatedTable = viewChild(RelatedRiskAssessmentReportsTableComponent);

  readonly table = viewChild(RiskAssessmentReportsTableComponent);

  readonly translations: any = {
    relatedRecordsTp: $localize`:@@ui.help:Display all of the latest completed risk assessments of records related to this record.`,
  };

  dialogRef: MatDialogRef<AddRiskAssessmentReportDialogComponent>;

  isLoadingRelatedRiskAssessmentReports = signal(true);

  relatedRiskAssessmentReports = signal<RiskAssessmentReportRecord[]>([]);

  subscriptions = new Subscription();

  currentRecordCount = computed(() => {
    if (this.table().isLoading()) {
      return undefined;
    }

    return this.table().totalElements();
  });

  relatedRecordsCount = computed(() => {
    if (!this.relatedTable() || this.relatedTable().isLoading()) {
      return undefined;
    }

    return this.relatedTable().totalElements();
  });

  constructor() {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openDialog() {
    this.dialogRef = this.dialog.open<
      AddRiskAssessmentReportDialogComponent,
      AddRiskAssessmentReportDialogData
    >(AddRiskAssessmentReportDialogComponent, {
      data: {
        orgId: this.activeOrganisationId(),
        resourceType: this.resourceType(),
        onSubmit: (template) => this.submit(template),
      },
      autoFocus: "dialog",
    });
  }

  async submit(template: string) {
    try {
      const report = await this.reportsApi.create({ template, recordUris: [this.recordUri()] });

      this.notification.showSuccess(
        $localize`:@@ui.riskassessmentreportcreated:Risk assessment report created`,
      );

      await this.table().getAll();

      this.overlayService().reportsCounter.set(this.table().totalElements());
      this.dialogRef.close();
      this.openReport(report, true);
    } catch (error) {
      this.notification.showError(error);
    }
  }

  openReport(report: RiskAssessmentReport | RiskAssessmentReportRecord, isEdit?: boolean) {
    this.router.navigate(
      this.router.getRiskAssessmentReportLink(report.id, true, {
        entityId: CommonUtils.getUriId(this.recordUri()),
        type: this.resourceType(),
        edit: isEdit,
      }),
    );
  }
}
